import { withAjaxSignal } from "../utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Merge react-select options by value (keeps latest label). */
export function mergeSelectOptions(existing = [], incoming = []) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((o) => {
    if (o?.value == null || o.value === "") return;
    map.set(String(o.value), o);
  });
  return [...map.values()];
}

/**
 * Async searchable select state (Alembic-style: load on type, hydrate selected ids only).
 * @param {Object} opts
 * @param {(args: { search: string, ids: string[] }) => Promise<Array<{value, label}>>} opts.fetchOptions
 * @param {string|string[]|null} opts.value - selected id(s)
 * @param {boolean} [opts.isMulti=false]
 * @param {boolean} [opts.enabled=true]
 */
export function useAsyncSelect({ fetchOptions, value, isMulti = false, enabled = true }) {
  const [cache, setCache] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchRef = useRef(fetchOptions);
  fetchRef.current = fetchOptions;

  const selectedIds = useMemo(() => {
    if (isMulti) {
      const arr = Array.isArray(value) ? value : [];
      return [...new Set(arr.map((x) => String(x ?? "").trim()).filter(Boolean))];
    }
    const v = String(value ?? "").trim();
    return v ? [v] : [];
  }, [value, isMulti]);

  const selectedKey = selectedIds.join("\u0001");

  // Search-as-you-type: wait for a short pause in typing, and when a newer search starts, cancel the
  // older one (timer or in-flight request) so the server only works on the latest text. The default
  // list (empty search) is never cancelled and identical searches share one request.
  const searchRef = useRef({ timer: null, ctrl: null, resolve: null, key: null, promise: null, seq: 0 });
  useEffect(
    () => () => {
      const r = searchRef.current;
      clearTimeout(r.timer);
      r.ctrl?.abort();
    },
    []
  );

  const loadOptions = useCallback(
    (inputValue = "") => {
      if (!enabled) return Promise.resolve([]);
      const search = String(inputValue || "").trim();
      const r = searchRef.current;
      if (r.promise && r.key === search) return r.promise;
      if (r.key) {
        // only a previous typed search is cancelled; the default list (r.key === "") keeps loading
        clearTimeout(r.timer);
        r.ctrl?.abort();
        r.resolve?.([]); // stale – react-select ignores results of superseded searches
      }
      const seq = r.seq + 1;
      r.seq = seq;
      r.key = search;
      const promise = new Promise((resolve) => {
        r.resolve = resolve;
        const run = async () => {
          const ctrl = new AbortController();
          r.ctrl = ctrl;
          setLoading(true);
          try {
            const opts = await withAjaxSignal(ctrl.signal, () => fetchRef.current({ search, ids: [] }));
            if (ctrl.signal.aborted) return resolve([]);
            const list = Array.isArray(opts) ? opts : [];
            setCache((prev) => mergeSelectOptions(prev, list));
            resolve(list);
          } catch {
            resolve([]);
          } finally {
            if (r.seq === seq) {
              setLoading(false);
              r.key = null;
              r.promise = null;
              r.ctrl = null;
            }
          }
        };
        if (search) r.timer = setTimeout(run, 300);
        else run();
      });
      r.promise = promise;
      return promise;
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled || !selectedIds.length) return undefined;
    const missing = selectedIds.filter((id) => !cache.some((o) => String(o.value) === id));
    if (!missing.length) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const opts = await fetchRef.current({ search: "", ids: missing });
        if (!cancelled) {
          setCache((prev) => mergeSelectOptions(prev, opts || []));
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, enabled]);

  const resolvedValue = useMemo(() => {
    if (isMulti) {
      return selectedIds.map((id) => cache.find((o) => String(o.value) === id) || { value: id, label: id });
    }
    if (!selectedIds.length) return null;
    const id = selectedIds[0];
    return cache.find((o) => String(o.value) === id) || { value: id, label: id };
  }, [cache, selectedIds, isMulti]);

  const mergeOptions = useCallback((incoming) => {
    setCache((prev) => mergeSelectOptions(prev, incoming));
  }, []);

  return {
    loadOptions,
    resolvedValue,
    defaultOptions: cache,
    isLoading: loading,
    mergeOptions,
    setCache,
  };
}
